import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../../../supabaseClient';
import ShortsGallery from './ShortsGallery';
import ShortsFeed from './ShortsFeed';

export default function ShortsPage() {
  const [shortsVideos, setShortsVideos] = useState<any[]>([]);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  // 1. Guarda os IDs já utilizados nesta sessão por segurança
  const usedVideoIds = useRef<Set<string>>(new Set());
  
  // 2. Gera uma semente única (seed) para esta sessão do utilizador
  const sessionSeed = useRef<string>(Math.random().toString(36).substring(2, 15));

  const params = new URLSearchParams(window.location.search);
  const urlVideoId = params.get('v');
  const [selectedVideoId, setSelectedVideoId] = useState<string | null>(urlVideoId);
  
  // 🚀 AJUSTE 1: Controla se é a primeira vez que entra, para auto-reproduzir o primeiro vídeo
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const LIMIT = 20;

  const fetchShortsFromSupabase = async (pageIndex: number) => {
    if (loading || !hasMore) return;
    setLoading(true);

    try {
      // Se vier de uma pesquisa (página 0 e tem ID na URL), busca esse vídeo primeiro!
      let videoPesquisado = null;
      if (pageIndex === 0 && urlVideoId) {
        const { data: specific } = await supabase.from('videos').select('*').eq('id', urlVideoId).single();
        if (specific) videoPesquisado = specific;
      }

      // 🧠 LÊ A MENTE DO UTILIZADOR (Puxa os gostos salvos no telemóvel)
      const savedPrefs = typeof window !== 'undefined' ? localStorage.getItem('doisplay_user_prefs') : null;
      const categoriasFavoritas = savedPrefs ? JSON.parse(savedPrefs) : [];

      let data, error;

      // 🚀 BIFURCAÇÃO INTELIGENTE: Se o telemóvel já aprendeu os gostos dele...
     // 🚀 BIFURCAÇÃO INTELIGENTE: Se o telemóvel já aprendeu os gostos dele...
      if (categoriasFavoritas.length > 0) {
        let res = await supabase.rpc('get_recommended_shorts', {
          p_seed: sessionSeed.current,
          p_limit: LIMIT,
          p_offset: pageIndex * LIMIT,
          p_categorias: categoriasFavoritas 
        });

        // 🛡️ O SALVA-VIDAS (PLANO B): Se o Supabase der erro na função inteligente, 
        // engole o erro e puxa o feed aleatório normal para a tela do utilizador não travar!
        if (res.error) {
          console.warn('Algoritmo falhou, a usar feed aleatório de segurança...', res.error);
          res = await supabase.rpc('get_seeded_random_shorts', {
            p_seed: sessionSeed.current,
            p_limit: LIMIT,
            p_offset: pageIndex * LIMIT
          });
        }
        
        data = res.data;
        error = res.error;
        
      } else {
        // Se for um utilizador totalmente novo, usa a roleta normal
        const res = await supabase.rpc('get_seeded_random_shorts', {
          p_seed: sessionSeed.current,
          p_limit: LIMIT,
          p_offset: pageIndex * LIMIT
        });
        data = res.data;
        error = res.error;
      }

      if (error) {
        console.error('Erro ao carregar Shorts:', error);
        return;
      }

      if (!data || data.length === 0) {
        setHasMore(false);
        return;
      }

      // 4. Remove vídeos que já foram exibidos nesta sessão (trava extra)
      let novosVideos = data.filter((video: any) => {
        // Evita duplicar se o vídeo pesquisado também vier no random
        if (videoPesquisado && video.id === videoPesquisado.id) return false; 
        if (usedVideoIds.current.has(video.id)) return false;
        
        usedVideoIds.current.add(video.id);
        return true;
      });

      // Injeta o vídeo pesquisado exatamente no topo da lista (posição 0)
      if (videoPesquisado) {
        novosVideos = [videoPesquisado, ...novosVideos];
        usedVideoIds.current.add(videoPesquisado.id);
      }

      if (novosVideos.length > 0) {
        setShortsVideos(prev => {
            const newList = [...prev, ...novosVideos];
            
            // 🚀 AJUSTE 2: Se for a primeira vez e NÃO houver ID na URL, abre logo o primeiro vídeo da lista!
            if (isInitialLoad && !urlVideoId && pageIndex === 0) {
                setSelectedVideoId(newList[0].id);
                setIsInitialLoad(false);
            }
            return newList;
        });
      }

      if (data.length < LIMIT) {
        setHasMore(false); // Chegou ao fim do catálogo real
      }

    } catch (err) {
      console.error('Erro ao carregar Shorts:', err);
    } finally {
      setLoading(false);
    }
  };

  // Primeira carga
  useEffect(() => {
    usedVideoIds.current.clear();
    setShortsVideos([]);
    setPage(0);
    setHasMore(true);
    fetchShortsFromSupabase(0);
  }, []);

  const handleLoadMore = () => {
    if (loading || !hasMore) return;
    const nextPage = page + 1;
    setPage(nextPage);
    fetchShortsFromSupabase(nextPage);
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [selectedVideoId]);

 return (
    <div style={{ 
      position: 'relative', 
      minHeight: '100vh', 
      paddingBottom: selectedVideoId ? '0px' : '95px', 
      backgroundColor: '#000', 
      // 🚀 Correção: Apenas trava TUDO se o vídeo estiver aberto. Senão, deixa o scroll livre e solto!
      ...(selectedVideoId && { height: '100dvh', overflow: 'hidden', overscrollBehavior: 'none' })
    }}>

      {selectedVideoId ? (
        <ShortsFeed
          videos={shortsVideos}
          initialVideoId={selectedVideoId}
          onLoadMore={handleLoadMore} // 👈 SÓ ADICIONAR ESTA LINHA AQUI!
          onBack={() => {
            setSelectedVideoId(null);
            if (window.history.replaceState) {
              window.history.replaceState({}, document.title, window.location.pathname);
            }
            window.scrollTo({ top: 0, behavior: 'instant' });
          }}
        />
      ) : (
        <ShortsGallery
          videos={shortsVideos}
          onSelectVideo={(id) => setSelectedVideoId(id)}
          onLoadMore={handleLoadMore}
          hasMoreFromSupabase={hasMore}
        />
      )}

      <div style={{ position: 'fixed', bottom: 0, left: 0, width: '100%', zIndex: 9999, pointerEvents: 'none' }} />
    </div>
  );
}